import { createContext, useContext, useReducer, useMemo } from 'react';

// 功能開關配置
const FEATURE_FLAGS = {
  ENABLE_KLING_ACTIONS: false,        // 是否啟用 Kling 動作功能
  ENABLE_TEAMSYNC_API: true,          // 是否啟用 Teamsync API
};

const initialState = {
  // ===== 基本資料 =====
  basic: {
    name: null,
    birthDate: null,         // ISO 字串格式: "2024-01-01" 或 null
    gender: "male",
    familyName: null,
    familyPhone: null,
    familyEmail: null,
    funeralCompany: null,    // 統一為 null，讓用戶自行選擇
    funeralDate: null,       // ISO 字串格式: "2024-01-01" 或 null
    obituary: [],      // 檔案業務物件陣列: [{ fileId, section: 'basic', category: 'uploaded', type: 'obituary', addedAt }]
    voiceFile: [],     // 檔案業務物件陣列: [{ fileId, section: 'basic', category: 'uploaded', type: 'voiceFile', addedAt }]
  },

  // ===== 典禮/儀式資料（按 UI 組織） =====
  ceremony: {
    // 公祭典禮
    public: {
      script: null,
      files: {
        my: {
          audio: [],     // [{ fileId, addedAt }]
          photo: []      // [{ fileId, addedAt }]
        },
        created: {
          audio: [],     // [{ fileId, addedAt }]
          video: []      // [{ fileId, addedAt }]
        },
        selected: {
          audio: [],           // 改為陣列格式
          video: [],           // 改為陣列格式
          createdAudio: []     // 改為陣列格式
        }
      },
      settings: {
        videoFormat: 'vertical',    // 視頻格式
        lipSync: 'hedra',          // 唇語同步模型
        languageModel: 'chinese'   // 語言模型
      }
    },

    // 家祭典禮
    family: {
      script: null,
      files: {
        my: { audio: [], photo: [] },
        created: { audio: [], video: [] },
        selected: {
          audio: [],           // 改為陣列格式
          video: [],           // 改為陣列格式
          createdAudio: []     // 改為陣列格式
        }
      },
      settings: {
        videoFormat: 'vertical',
        lipSync: 'hedra',
        languageModel: 'chinese'
      }
    },

    // 遠行典禮
    farewell: {
      script: null,
      files: {
        my: { audio: [], photo: [] },
        created: { audio: [], video: [] },
        selected: {
          audio: [],           // 改為陣列格式
          video: [],           // 改為陣列格式
          createdAudio: []     // 改為陣列格式
        }
      },
      settings: {
        videoFormat: 'vertical',
        lipSync: 'hedra',
        languageModel: 'chinese'
      }
    },

    // 直播互動影片
    liveStream: {
      isEnabled: false,
      klingAction: "all", // Kling 動作描述，預設為 smile
      files: {
        my: {
          photo: [],              // [{ fileId, addedAt }]
          background: []          // [{ fileId, addedAt }]
        },
        created: { video: [] },         // [{ fileId, addedAt }]
        selected: {
          photo: [],           // 改為陣列格式
          background: []       // 改為陣列格式
        }
      },
      settings: {
        videoFormat: 'vertical',
        lipSync: 'hedra'               // 預設使用 Hedra 對嘴模組
      }
    },

    // 動作呈現（動態陣列）
    actions: [
      {
        id: 'action_1',
        order: 1,
        files: {
          my: { photo: [] },              // [{ fileId, addedAt, isSelected }]
          created: { video: [] }          // [{ fileId, addedAt, isSelected }]
        },
        settings: {
          videoFormat: 'vertical',
          lipSync: 'kling'
        },
        action: null,          // 動作類型
        klingAction: FEATURE_FLAGS.ENABLE_KLING_ACTIONS ? 'smile' : null,  // Kling 專用動作類型
        moreText: null,        // 自定義動作描述
        hint: null,            // 提示文字
        reverseHint: null      // 反向提示
      }
    ]
  },

  // ===== 其他 =====
  notes: null,
  price: 0,
  live: {
    isEnabled: false,
    roomCode: null,
    url: null,
    // 直播互動影片 Teamsync 相關（功能開關控制）
    ...(FEATURE_FLAGS.ENABLE_TEAMSYNC_API && {
      liveStreamApiKey: null,      // 直播互動影片的 API Key
      liveStreamChatroomId: null,  // 直播互動影片的聊天室 ID
      liveStreamChatroomName: null // 直播互動影片的聊天室名稱
    })
  },

  // ===== 版本管理 =====
  version: {
    // 控制邏輯
    allowExtraVersions: false,      // 是否允許超過3個版本
    currentVersionNumber: 1,        // 當前版本號
    totalVersions: 0,               // 總版本數
    isEdited: true,                 // 是否已編輯
    isCreatedByBackend: false,       // 是否由後端創建（統一為 true）

    // 版本數據
    sourceItem: [],                 // 版本歷史數據
    sourceBlob: [],                 // blob 數據
    sourceJsonFileId: null,         // JSON 檔案 ID
    selectedVersion: 0              // 選中的版本索引
  },

  // ===== 保存操作狀態 =====
  saveOperation: {
    isLoading: false,               // 保存載入中
    error: null,                    // 保存錯誤信息
    progress: 0                     // 保存進度 (0-100)
  }
};

// 定義 action types
export const ACTIONS = {
  // ===== 檔案管理 =====
  UPLOAD_FILE: 'UPLOAD_FILE',
  REMOVE_FILE: 'REMOVE_FILE',
  SELECT_FILE: 'SELECT_FILE',

  // ===== 設定管理 =====
  SET_VIDEO_FORMAT: 'SET_VIDEO_FORMAT',
  SET_LIP_SYNC: 'SET_LIP_SYNC',
  SET_LANGUAGE_MODEL: 'SET_LANGUAGE_MODEL',

  // ===== 動作管理 =====
  ADD_ACTION: 'ADD_ACTION',
  REMOVE_ACTION: 'REMOVE_ACTION',
  UPDATE_ACTION: 'UPDATE_ACTION',

  // ===== 內容管理 =====
  UPDATE_SCRIPT: 'UPDATE_SCRIPT',
  UPDATE_NOTES: 'UPDATE_NOTES',

  // ===== 版本管理 =====
  SET_ALLOW_EXTRA_VERSIONS: 'SET_ALLOW_EXTRA_VERSIONS',
  SET_VERSION_INFO: 'SET_VERSION_INFO',
  UPDATE_VERSION: 'UPDATE_VERSION',
  SET_SELECTED_VERSION: 'SET_SELECTED_VERSION',

  // ===== 保存操作 =====
  SET_SAVE_LOADING: 'SET_SAVE_LOADING',
  SET_SAVE_ERROR: 'SET_SAVE_ERROR',
  SET_SAVE_PROGRESS: 'SET_SAVE_PROGRESS',
  RESET_SAVE_STATE: 'RESET_SAVE_STATE',

  // ===== 數據管理 =====
  SET_CEREMONY_DATA: 'SET_CEREMONY_DATA',
  UPDATE_LIVE_STREAM_VIDEO_STATUS: 'UPDATE_LIVE_STREAM_VIDEO_STATUS', // 直播互動影片狀態
  SET_BASIC_DATA: 'SET_BASIC_DATA',
  UPDATE_BASIC_FIELD: 'UPDATE_BASIC_FIELD',
  RESET_STATE: 'RESET_STATE',

  // ===== 直播管理 =====
  UPDATE_LIVE_URL: 'UPDATE_LIVE_URL',
  UPDATE_LIVE_ROOM_CODE: 'UPDATE_LIVE_ROOM_CODE',
  UPDATE_LIVE_STATUS: 'UPDATE_LIVE_STATUS',
  UPDATE_LIVE_STREAM_STATUS: 'UPDATE_LIVE_STREAM_STATUS',

  // ===== 訂單管理 =====
  UPDATE_PRICE: 'UPDATE_PRICE',

  // ===== Kling 管理 =====
  UPDATE_KLING_ACTION: 'UPDATE_KLING_ACTION',
  UPDATE_ACTION_KLING_ACTION: 'UPDATE_ACTION_KLING_ACTION',

  // ===== 直播互動影片 Teamsync 管理 =====
  UPDATE_LIVE_STREAM_API_KEY: 'UPDATE_LIVE_STREAM_API_KEY',
  UPDATE_LIVE_STREAM_CHATROOM: 'UPDATE_LIVE_STREAM_CHATROOM',
};

// 定義 section types
export const SECTIONS = {
  PUBLIC: 'public',
  FAMILY: 'family',
  FAREWELL: 'farewell',
  ACTION1: 'action1',
  ACTION2: 'action2',
  LIVE_STREAM: 'liveStream',
};

// Reducer 函數
const demandReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case ACTIONS.UPLOAD_FILE: {
      const { file, formField } = payload;
      if (!formField || !Array.isArray(formField) || formField.length === 0) {
        console.warn('Invalid formField in UPLOAD_FILE action');
        return state;
      }

      // 創建檔案業務物件
      const fileBusinessObject = {
        fileId: file.id || file.uid,
        fileName: file.name,
        ...(file.duration ? { duration: file.duration } : {}),
        ...(file.originalWidth ? { originalWidth: file.originalWidth } : {}),
        ...(file.originalHeight ? { originalHeight: file.originalHeight } : {}),
      };

      // 根據 formField 更新狀態
      let newState = { ...state };
      let current = newState;

      // 遍歷 formField 路徑
      for (let i = 0; i < formField.length - 1; i++) {
        const field = formField[i];
        if (!current[field]) {
          current[field] = {};
        }
        current = current[field];
      }

      // 最後一個欄位是陣列
      const lastField = formField[formField.length - 1];
      if (!current[lastField]) {
        current[lastField] = [];
      }

      // 檢查是否已存在相同的檔案
      const isDuplicate = current[lastField].some(
        existingFile => existingFile.fileId === fileBusinessObject.fileId
      );

      if (!isDuplicate) {
        current[lastField] = [...current[lastField], fileBusinessObject];
      } else {
        console.warn('File already exists in this location:', fileBusinessObject.fileId);
      }

      return newState;
    }

    case ACTIONS.REMOVE_FILE: {
      const { fileId, formField } = payload;
      if (!formField || !Array.isArray(formField) || formField.length === 0) {
        console.warn('Invalid formField in REMOVE_FILE action');
        return state;
      }

      // 根據 formField 更新狀態
      let newState = { ...state };
      let current = newState;

      // 遍歷到倒數第二層
      for (let i = 0; i < formField.length - 1; i++) {
        const field = formField[i];
        if (!current[field]) {
          return state; // 路徑不存在，返回原狀態
        }
        current = current[field];
      }

      // 最後一個欄位是陣列，過濾掉要刪除的檔案
      const lastField = formField[formField.length - 1];
      if (!current[lastField]) {
        return state;
      }
      current[lastField] = current[lastField].filter(
        fileObj => fileObj.fileId !== fileId
      );

      return newState;
    }

    case ACTIONS.SELECT_FILE: {
      console.log('[DemandContext] SELECT_FILE 參數:', payload);
      const { value, formField } = payload;
      if (!formField || !Array.isArray(formField) || formField.length === 0) {
        console.warn('Invalid formField in SELECT_FILE action');
        return state;
      }

      // 根據 formField 更新狀態
      let newState = { ...state };
      let current = newState;

      // 遍歷到倒數第二層
      for (let i = 0; i < formField.length - 1; i++) {
        const field = formField[i];
        if (!current[field]) {
          current[field] = {};
        }
        current = current[field];
      }

      // 最後一個欄位直接賦值（selected 欄位）
      const lastField = formField[formField.length - 1];
      current[lastField] = value; // value 已經是正確的陣列格式：[{ fileId }]

      return newState;
    }

    case ACTIONS.SET_VIDEO_FORMAT: {
      const { section, value } = payload;

      // 檢查是否為動作 section
      if (section?.startsWith?.('action_')) {
        const updatedActions = state.ceremony.actions.map(action =>
          action.id === section
            ? {
              ...action,
              settings: {
                ...action.settings,
                videoFormat: value
              }
            }
            : action
        );

        return {
          ...state,
          ceremony: {
            ...state.ceremony,
            actions: updatedActions
          }
        };
      }

      // 原有的非動作 section 邏輯，但要先檢查 section 是否存在
      if (!state.ceremony[section]) {
        console.warn(`Section "${section}" not found in ceremony state`);
        return state;
      }

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          [section]: {
            ...state.ceremony[section],
            settings: {
              ...state.ceremony[section].settings,
              videoFormat: value
            }
          }
        }
      };
    }

    case ACTIONS.SET_LIP_SYNC: {
      const { section, value } = payload;

      // 檢查是否為動作 section
      if (section?.startsWith?.('action_')) {
        const updatedActions = state.ceremony.actions.map(action =>
          action.id === section
            ? {
              ...action,
              settings: {
                ...action.settings,
                lipSync: value
              }
            }
            : action
        );

        return {
          ...state,
          ceremony: {
            ...state.ceremony,
            actions: updatedActions
          }
        };
      }

      // 原有的非動作 section 邏輯，但要先檢查 section 是否存在
      if (!state.ceremony[section]) {
        console.warn(`Section "${section}" not found in ceremony state`);
        return state;
      }

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          [section]: {
            ...state.ceremony[section],
            settings: {
              ...state.ceremony[section].settings,
              lipSync: value
            }
          }
        }
      };
    }

    case ACTIONS.SET_LANGUAGE_MODEL: {
      const { section, value } = payload;

      // 檢查 ceremony section 是否存在
      if (!state.ceremony[section]) {
        console.warn(`Section "${section}" not found in ceremony state`);
        return state;
      }

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          [section]: {
            ...state.ceremony[section],
            settings: {
              ...state.ceremony[section].settings,
              languageModel: value
            }
          }
        }
      };
    }

    case ACTIONS.ADD_ACTION: {
      const newActionId = `action_${Date.now()}`;
      const newOrder = state.ceremony.actions.length + 1;
      const newAction = {
        id: newActionId,
        order: newOrder,
        files: {
          my: { photo: [] },              // [{ fileId, addedAt, isSelected }]
          created: { video: [] }          // [{ fileId, addedAt, isSelected }]
        },
        settings: {
          videoFormat: 'vertical',
          lipSync: 'kling'
        },
        action: null,          // 動作類型
        klingAction: FEATURE_FLAGS.ENABLE_KLING_ACTIONS ? 'smile' : null,  // Kling 專用動作類型
        moreText: null,        // 自定義動作描述
        hint: null,            // 提示文字
        reverseHint: null      // 反向提示
      };

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          actions: [...state.ceremony.actions, newAction]
        }
      };
    }

    case ACTIONS.REMOVE_ACTION: {
      const { actionId } = payload;
      const filteredActions = state.ceremony.actions.filter(action => action.id !== actionId);

      // 重新排序
      const reorderedActions = filteredActions.map((action, index) => ({
        ...action,
        order: index + 1
      }));

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          actions: reorderedActions
        }
      };
    }

    case ACTIONS.UPDATE_ACTION: {
      const { actionId, updates } = payload;
      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          actions: state.ceremony.actions.map(action =>
            action.id === actionId
              ? { ...action, ...updates }
              : action
          )
        }
      };
    }

    case ACTIONS.UPDATE_SCRIPT: {
      const { section, script } = payload;
      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          [section]: {
            ...state.ceremony?.[section],
            script
          }
        }
      };
    }

    case ACTIONS.UPDATE_NOTES: {
      return {
        ...state,
        notes: payload
      };
    }

    case ACTIONS.SET_BASIC_DATA:
      return {
        ...state,
        basic: {
          ...state.basic,
          ...payload
        }
      };

    case ACTIONS.UPDATE_BASIC_FIELD: {
      const { field, value } = payload;
      return {
        ...state,
        basic: {
          ...state.basic,
          [field]: value
        }
      };
    }

    case ACTIONS.SET_CEREMONY_DATA:
      console.log('[DemandContext] SET_CEREMONY_DATA 參數:', payload);
      return {
        ...state,
        // 如果有 basic 數據，更新 basic
        basic: {
          ...state.basic,
          ...(payload.basic || {})
        },
        ceremony: {
          ...state.ceremony,
          ...(payload.ceremony || {})
        },
        notes: payload.notes || state.notes,
        // 添加 price 和 live 數據的更新
        price: payload.price ?? state.price,
        live: {
          ...state.live,
          ...(payload.live || {})
        }
      };

    // isLoading 狀態已移至 MainContainer 本地管理

    // ===== 版本管理 =====
    case ACTIONS.SET_ALLOW_EXTRA_VERSIONS:
      return {
        ...state,
        version: {
          ...state.version,
          allowExtraVersions: payload
        }
      };

    case ACTIONS.SET_VERSION_INFO:
      return {
        ...state,
        version: {
          ...state.version,
          ...payload
        }
      };

    case ACTIONS.UPDATE_VERSION:
      return {
        ...state,
        version: {
          ...state.version,
          ...payload
        }
      };

    case ACTIONS.SET_SELECTED_VERSION:
      return {
        ...state,
        version: {
          ...state.version,
          selectedVersion: payload
        }
      };

    // ===== 保存操作管理 =====
    case ACTIONS.SET_SAVE_LOADING:
      return {
        ...state,
        saveOperation: {
          ...state.saveOperation,
          isLoading: payload
        }
      };

    case ACTIONS.SET_SAVE_ERROR:
      return {
        ...state,
        saveOperation: {
          ...state.saveOperation,
          error: payload
        }
      };

    case ACTIONS.SET_SAVE_PROGRESS:
      return {
        ...state,
        saveOperation: {
          ...state.saveOperation,
          progress: payload
        }
      };

    case ACTIONS.RESET_SAVE_STATE:
      return {
        ...state,
        saveOperation: {
          isLoading: false,
          error: null,
          progress: 0
        }
      };

    case ACTIONS.RESET_STATE:
      return initialState;

    // ===== 直播管理 =====
    case ACTIONS.UPDATE_LIVE_URL:
      return {
        ...state,
        live: {
          ...state.live,
          url: payload
        }
      };

    case ACTIONS.UPDATE_LIVE_ROOM_CODE:
      return {
        ...state,
        live: {
          ...state.live,
          roomCode: payload
        }
      };

    case ACTIONS.UPDATE_LIVE_STATUS:
      return {
        ...state,
        live: {
          ...state.live,
          isEnabled: payload
        }
      };

    case ACTIONS.UPDATE_LIVE_STREAM_STATUS:
      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          liveStream: {
            ...state.ceremony.liveStream,
            isEnabled: payload
          }
        }
      };

    case ACTIONS.UPDATE_LIVE_STREAM_VIDEO_STATUS:
      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          liveStream: {
            ...state.ceremony.liveStream,
            isEnabled: payload
          }
        }
      };

    case ACTIONS.UPDATE_PRICE:
      return {
        ...state,
        price: payload
      };

    // ===== Kling 管理（功能開關控制）=====
    case ACTIONS.UPDATE_KLING_ACTION:
      if (!FEATURE_FLAGS.ENABLE_KLING_ACTIONS) return state;
      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          liveStream: {
            ...state.ceremony.liveStream,
            klingAction: payload
          }
        }
      };

    case ACTIONS.UPDATE_ACTION_KLING_ACTION:
      if (!FEATURE_FLAGS.ENABLE_KLING_ACTIONS) return state;
      const { actionId, klingAction } = payload;
      const updatedActionsForKling = state.ceremony.actions.map(action =>
        action.id === actionId ? { ...action, klingAction } : action
      );

      return {
        ...state,
        ceremony: {
          ...state.ceremony,
          actions: updatedActionsForKling
        }
      };

    // ===== 直播互動影片 Teamsync 管理（功能開關控制）=====
    case ACTIONS.UPDATE_LIVE_STREAM_API_KEY:
      if (!FEATURE_FLAGS.ENABLE_TEAMSYNC_API) return state;
      return {
        ...state,
        live: {
          ...state.live,
          liveStreamApiKey: payload
        }
      };

    case ACTIONS.UPDATE_LIVE_STREAM_CHATROOM:
      if (!FEATURE_FLAGS.ENABLE_TEAMSYNC_API) return state;
      return {
        ...state,
        live: {
          ...state.live,
          liveStreamChatroomId: payload.chatroomId,
          liveStreamChatroomName: payload.chatroomName
        }
      };

    default:
      return state;
  }
}

// 建立 Context
const DemandContext = createContext();

// Context Provider 元件
export function DemandProvider({ children }) {
  const [state, dispatch] = useReducer(demandReducer, initialState);

  const value = useMemo(() => ({
    state,
    dispatch
  }), [state]);

  return (
    <DemandContext.Provider value={value}>
      {children}
    </DemandContext.Provider>
  );
}

// 自定義 Hook 以方便使用 Context
export function useDemand() {
  const context = useContext(DemandContext);
  if (!context) {
    throw new Error('useDemand must be used within a DemandProvider');
  }
  return context;
}

// 🎯 核心檔案業務邏輯 Hooks

/**
 * 獲取指定位置的檔案 ID 陣列
 */
export const useFileIds = (section, category, fileType) => {
  const { state } = useDemand();

  if (section === 'basic') {
    return (state.basic[fileType] || []).map(fileObj => fileObj.fileId);
  }

  if (section.startsWith('action_')) {
    const action = state.ceremony.actions.find(a => a.id === section);
    if (category === 'selected') {
      return action?.files?.selected?.[fileType] || [];
    }
    return action?.files?.[category]?.[fileType]?.map(fileObj => fileObj.fileId) || [];
  }

  const ceremonySection = state.ceremony[section];
  if (category === 'selected') {
    return ceremonySection?.files?.selected?.[fileType] || [];
  }
  return ceremonySection?.files?.[category]?.[fileType]?.map(fileObj => fileObj.fileId) || [];
};

/**
 * 獲取指定位置的檔案業務物件陣列
 */
export const useFileBusinessObjects = (section, category, fileType) => {
  const { state } = useDemand();

  if (section === 'basic') {
    return state.basic[fileType] || [];
  }

  if (section.startsWith('action_')) {
    const action = state.ceremony.actions.find(a => a.id === section);
    return action?.files?.[category]?.[fileType] || [];
  }

  const ceremonySection = state.ceremony[section];
  return ceremonySection?.files?.[category]?.[fileType] || [];
};

// useSelectedFileId hook 可以被標記為 deprecated，但暫時保留以支持向後兼容
/**
 * @deprecated Use useFileIds(section, 'selected', fileType) instead
 */
export const useSelectedFileId = (section, fileType) => {
  const { state } = useDemand();

  if (section.startsWith('action_')) {
    // 動作區段暫時不支援選中邏輯
    return [];
  }

  const ceremonySection = state.ceremony[section];
  return ceremonySection?.files?.selected?.[fileType] || [];
};
